import React, {useEffect,useRef,useState} from "react";
import {Alert,Button,Form,Table} from "react-bootstrap";
import axios from "axios";
import {API_URL} from "../../../../config";
import {isCalendarDate, overrideReasonWords, shortDate} from "./rsoForecastWindowHelpers";
import RsoSignInExpired, {isSignInExpired} from "./RsoSignInExpired";

const missing = "Not recorded";
const text = (value) => typeof value === "string" && value.trim() ? value : missing;
const number = (value) => value === null || value === undefined || value === "" || !Number.isFinite(Number(value))
  ? missing : Number(value).toLocaleString(undefined,{maximumFractionDigits:20});
const unit = (value) => ({SF:"sq ft",SY:"sq yd"}[String(value).toUpperCase()] || text(value));
const date = (value) => value && !Number.isNaN(new Date(value).getTime()) ? new Date(value).toLocaleString() : missing;
// Forecasts carry YYYY-MM-01; material changes carry YYYY-MM.
const month = (value) => /^\d{4}-\d{2}(-01)?$/.test(value || "") ? new Date(`${value.slice(0,7)}-01T12:00:00`).toLocaleDateString(undefined,{month:"long",year:"numeric"}) : missing;
const amount = (line) => line.quantity == null ? missing : `${number(line.quantity)} ${unit(line.uom)}`;
const emptyFilters = {search:"",eventDate:"",property:"",requestType:"",material:"",turns:"",quantity:""};
const normalize = (value) => String(value ?? "").toLocaleLowerCase().replace(/,/g, "").trim();
const matches = (value,query) => normalize(value).includes(normalize(query));
const allowedRequestTypes = new Set(["forecast","material_change"]);
const validRequestStatus = (item) => item.requestType === "forecast"
  ? item.requestStatus == null || item.requestStatus === "sent"
  : item.requestStatus === "sent" || (item.requestStatus === "saved" && !!item.savedAt)
    || (item.requestStatus === "discarded" && !!item.savedAt && !!item.discardedAt)
    || (item.requestStatus === "withdrawn" && !!item.sentAt && !!item.withdrawnAt)
    || (item.requestStatus === "replaced" && !!item.savedAt);
const isSaved = (item) => item.requestType === "material_change" && item.requestStatus === "saved";
// "No units this month" for a property and month: no demand, possibly zero-clears of earlier units.
const isNoUnits = (item) => item.requestType === "forecast" && item.noUnits === true;
const noUnitsLabel = "No units this month";
const clearedNames = (item) => item.lines.map((line) => text(line.materialName)).join(", ");
const isDiscarded = (item) => item.requestType === "material_change" && item.requestStatus === "discarded";
// A withdrawn request was sent, then withdrawn by Sales; a replaced request was never sent.
const isWithdrawn = (item) => item.requestType === "material_change" && item.requestStatus === "withdrawn";
// A forecast Sales sent outside the input week, with a sign-off (listed in full in the Overrides tab).
const windowOverride = (item) => item.requestType === "forecast" && item.windowOverride !== null && typeof item.windowOverride === "object"
  ? item.windowOverride : null;
const overrideDetails = (item) => {
  const signOff = windowOverride(item);
  const reason = overrideReasonWords(signOff.reason);
  const told = isCalendarDate(signOff.toldPurchasingOn) ? shortDate(signOff.toldPurchasingOn) : missing;
  return <p>Sent as an override{reason ? ` (${reason})` : ""}. Sales told Purchasing on {told}: <span style={{whiteSpace:"pre-wrap"}}>{text(signOff.note)}</span></p>;
};
const isReplaced = (item) => item.requestType === "material_change" && item.requestStatus === "replaced";
// The API orders and pages newest first by each request's latest activity: withdrawnAt,
// else discardedAt, else sentAt, else savedAt. Rows are dated by that same key so the list
// stays in order; a withdrawn or discarded request shows when it was sent or saved beneath it.
const laterEventAt = (item) => isWithdrawn(item) ? item.withdrawnAt : isDiscarded(item) ? item.discardedAt : null;
const eventAt = (item) => laterEventAt(item) || item.sentAt || item.savedAt;
const earlierEventAt = (item) => laterEventAt(item) ? item.sentAt || item.savedAt : null;
const statusLabel = (item) => isWithdrawn(item) ? "Withdrawn" : isReplaced(item) ? "Replaced before sending"
  : isDiscarded(item) ? "Discarded" : isSaved(item) ? "Saved, awaiting Purchasing" : "Sent";
const earlierEvent = (item) => earlierEventAt(item) ? `${item.sentAt ? "Sent" : "Saved"} ${date(earlierEventAt(item))}` : null;
const quantityNote = (item) => isWithdrawn(item) ? "Withdrawn" : isReplaced(item) ? "Not sent" : isDiscarded(item) ? "Cancelled" : null;
const requestType = (item) => item.requestType === "material_change" ? `Material change${item.forecastMonth ? ` ${month(item.forecastMonth)}` : ""}`
  : item.requestType === "forecast" ? `Forecast ${month(item.forecastMonth)}` : missing;
// A permanent change has one saved line per affected floor plan. The request
// quantity is recorded on one line; the other lines are audit records with 0.
const materialChangeGroups = (lines) => {
  const groups = new Map();
  lines.forEach((line) => {
    const key = JSON.stringify([line.previousMaterialName || "",line.materialName || "",String(line.uom || "").toUpperCase()]);
    if (!groups.has(key)) groups.set(key,{...line,quantity:0,hasMissingQuantity:false,lines:[]});
    const group = groups.get(key);
    group.lines.push(line);
    if (line.quantity == null || line.quantity === "" || !Number.isFinite(Number(line.quantity))) group.hasMissingQuantity = true;
    else group.quantity += Number(line.quantity);
  });
  return [...groups.values()].map(({hasMissingQuantity,...group}) => ({...group,quantity:hasMissingQuantity ? null : group.quantity}));
};
const displayLines = (item) => item.requestType === "material_change" ? materialChangeGroups(item.lines) : item.lines;
const materialLabel = (line) => `${line.previousMaterialName ? `${line.previousMaterialName} → ` : ""}${text(line.materialName)}`;
const material = (item) => {
  if (isNoUnits(item)) return noUnitsLabel;
  const lines = displayLines(item);
  return lines.length ? lines.map(materialLabel).join(" ") : missing;
};
const quantity = (item) => {
  const lines = displayLines(item);
  return lines.length ? lines.map(amount).join(" ") : missing;
};
// A material change records one turn count for the whole request; a forecast has one per material.
const turnCounts = (item) => {
  const lines = displayLines(item);
  const count = (value) => value == null ? missing : number(value);
  return item.requestType === "material_change" || !lines.length ? [count(item.unitsTurning)]
    : lines.map((line) => count(line.unitsTurning ?? item.unitsTurning));
};
const lineTurns = (item,line) => item.requestType === "material_change" ? item.unitsTurning : line.unitsTurning ?? item.unitsTurning;
const localDate = (value) => {
  const parsed = new Date(value);
  if (!value || Number.isNaN(parsed.getTime())) return "";
  return `${parsed.getFullYear()}-${String(parsed.getMonth()+1).padStart(2,"0")}-${String(parsed.getDate()).padStart(2,"0")}`;
};
const matchesFilters = (item,filters) => {
  const fields = [date(eventAt(item)),text(item.propertyName),requestType(item),material(item),quantity(item),statusLabel(item),earlierEvent(item),turnCounts(item).join(" ")];
  return (!filters.search || fields.some((field) => matches(field,filters.search)))
    && (!filters.eventDate || [eventAt(item),earlierEventAt(item)].some((value) => localDate(value) === filters.eventDate))
    && (!filters.property || matches(fields[1],filters.property))
    && (!filters.requestType || item.requestType === filters.requestType)
    && (!filters.material || matches(fields[3],filters.material))
    && (!filters.turns || matches(fields[7],filters.turns))
    && (!filters.quantity || matches(fields[4],filters.quantity));
};
const basisDescription = (basis) => {
  if (!basis) return null;
  if (basis.calculationVersion === "max-flooring-area-v1"
    && basis.areaSqFt !== null && basis.areaSqFt !== undefined
    && basis.areaSqFt !== "" && Number.isFinite(Number(basis.areaSqFt))) {
    return `Shared largest flooring area: ${number(basis.areaSqFt)} sq ft · Floor plan: ${text(basis.floorPlanName)}`;
  }
  if (basis.calculationVersion === "max-default-layout-material-v1"
    && basis.maxDefaultLayoutQuantity !== null && basis.maxDefaultLayoutQuantity !== undefined
    && basis.maxDefaultLayoutQuantity !== "" && Number.isFinite(Number(basis.maxDefaultLayoutQuantity))) {
    return `Maximum Default Layout quantity: ${number(basis.maxDefaultLayoutQuantity)} ${unit(basis.uom)} · Floor plan: ${text(basis.floorPlanName)}`;
  }
  if (basis.areaSqFt !== null && basis.areaSqFt !== undefined && basis.areaSqFt !== ""
    && Number.isFinite(Number(basis.areaSqFt))) {
    return `Recorded flooring area: ${number(basis.areaSqFt)} sq ft · Floor plan: ${text(basis.floorPlanName)}`;
  }
  return `Calculation basis: ${missing}`;
};

function RequestDetails({item}) {
  if (isNoUnits(item)) return <div className="p-2" aria-label={`Request details for ${item.propertyName}`}>
    <p>Sent by: {text(item.submittedBy)}</p>
    {windowOverride(item) && overrideDetails(item)}
    <p><strong>{noUnitsLabel}</strong> for {month(item.forecastMonth)}. Purchasing won&apos;t plan RSO material for this property that month.</p>
    {!!item.lines.length && <p>Cleared to 0: {clearedNames(item)}</p>}
  </div>;
  return <div className="p-2" aria-label={`Request details for ${item.propertyName}`}>
      <p>{isSaved(item) || isDiscarded(item) || isReplaced(item) ? "Saved by" : "Sent by"}: {text(item.submittedBy)}</p>
      {windowOverride(item) && overrideDetails(item)}
      {isDiscarded(item) && <p>Discarded {date(item.discardedAt)}. The previous material was restored. This request was not sent to Purchasing.</p>}
      {isWithdrawn(item) && <p>Sent {date(item.sentAt)}, withdrawn {date(item.withdrawnAt)}. The previous material was restored and the withdrawal was sent to Purchasing.</p>}
      {isReplaced(item) && <p>A later material change replaced this saved request. It was never sent to Purchasing.</p>}
      {item.requestType === "material_change" && item.unitsTurning != null && <p>Forecasted Unit Turns: {number(item.unitsTurning)}{item.forecastMonth ? ` for ${month(item.forecastMonth)}` : ""}</p>}
      {item.requestType === "material_change" ? materialChangeGroups(item.lines).map((group,index) => {
        const floorPlans = [...new Set(group.lines.map((line) => line.floorPlanName).filter(Boolean))];
        const propertyBasis = group.basis?.calculationVersion === "largest-floor-plan-area-v1"
          && group.basis.areaSqFt != null && item.unitsTurning != null ? group.basis : null;
        return <div key={index} className="mb-3">
          <p><strong>{isDiscarded(item) ? "Discarded replacement" : isWithdrawn(item) ? "Withdrawn replacement" : "New material"}: {text(group.materialName)}</strong></p>
          <p>Previous material: {text(group.previousMaterialName)}</p>
          <p>{isDiscarded(item) ? "Cancelled request quantity" : isWithdrawn(item) ? "Withdrawn request quantity"
            : isReplaced(item) ? "Unsent request quantity" : "Requested quantity"}: {amount(group)}</p>
          {propertyBasis ? <p>Saved calculation basis: {number(propertyBasis.areaSqFt)} sq ft from {text(propertyBasis.floorPlanName)} × {number(item.unitsTurning)} Forecasted Unit Turns{String(group.uom).toUpperCase() === "SY" ? ", converted to sq yd" : ""}.</p>
            : <p>Saved calculation basis: Not recorded.</p>}
          {!!floorPlans.length && <details><summary>Affected floor plans ({floorPlans.length})</summary>
            <p>{isDiscarded(item) ? "These floor plans were restored when this material change was discarded. The quantity above is retained for history."
              : isWithdrawn(item) ? "These floor plans were restored when this sent change was withdrawn. The quantity above is retained for history."
              : isReplaced(item) ? "Each floor plan below received this material standard until a later change replaced it. The quantity above was never sent to Purchasing."
              : "The requested quantity above is one request for the property. Each floor plan below received the new material standard."}</p>
            <ul>{floorPlans.map((name) => <li key={name}>{name}</li>)}</ul>
          </details>}
        </div>;
      }) : <>
      {item.unitsTurning != null && <p>Forecasted Unit Turns: {number(item.unitsTurning)}</p>}
      {(item.layouts || []).map((layout,index) => <p key={index}>{text(layout.floorplanName)}: {number(layout.forecastUnits)} units</p>)}
      {(item.lines || []).map((line,index) => <div key={index} className="mb-3">
        <strong>{text(line.materialName)}</strong>
        {line.previousMaterialName && <p>Replaces: {line.previousMaterialName}</p>}
        {line.floorPlanName && <p>Floor plan: {line.floorPlanName}</p>}
        <p>Saved quantity: {amount(line)}</p>
        <p>Forecasted Unit Turns: {number(line.unitsTurning)}</p>
        <p>Calculated amount: {line.calculatedQuantity == null ? missing : `${number(line.calculatedQuantity)} ${unit(line.uom)}`}</p>
        {line.basis && <p>{basisDescription(line.basis)}</p>}
        <p style={{whiteSpace:"pre-wrap"}}>Sales notes: {text(line.notes)}</p>
      </div>)}
      </>}
      {!item.lines?.length && <p>Material and quantity: {missing}</p>}
    </div>;
}

function RequestRow({item}) {
  const [open,setOpen] = useState(false);
  const lines = displayLines(item);
  return <>
    <tr>
      <td>{date(eventAt(item))}{earlierEvent(item) && <small className="d-block">{earlierEvent(item)}</small>}</td><td>{text(item.propertyName)}</td>
      <td>{item.requestType === "material_change" ? <>Material change{item.forecastMonth && <><br /><small>{month(item.forecastMonth)}</small></>}</>
        : item.requestType === "forecast" ? <>Forecast<br /><small>{month(item.forecastMonth)}</small></> : missing}
        <div><small>{statusLabel(item)}</small>{windowOverride(item) && <span className="rsf-override-tag">Override</span>}</div></td>
      <td>{isNoUnits(item) ? <div className="rsf-history-material">
        <span className="rsf-history-material-name">{noUnitsLabel}</span>
        {!!item.lines.length && <span className="rsf-history-material-meta"><span><small>Cleared to 0:</small><span>{clearedNames(item)}</span></span></span>}
      </div> : lines.length ? <ul className="rsf-history-materials">{lines.map((line,index) => <li key={index} className="rsf-history-material">
        <span className="rsf-history-material-name">{materialLabel(line)}</span>
        <span className="rsf-history-material-meta">
          <span><small>Forecasted Unit Turns:</small><strong>{number(lineTurns(item,line))}</strong></span>
          <span><small>Quantity:</small><strong>{amount(line)}</strong>{quantityNote(item) && <small>{quantityNote(item)}</small>}</span>
        </span>
      </li>)}</ul> : <div className="rsf-history-material">
        <span className="rsf-history-material-name">{missing}</span>
        <span className="rsf-history-material-meta"><span><small>Forecasted Unit Turns:</small><strong>{number(item.unitsTurning)}</strong></span></span>
      </div>}</td>
      <td><Button variant="link" className="p-0" aria-expanded={open} onClick={() => setOpen(!open)}>{open ? "Hide details" : "View details"}</Button></td>
    </tr>
    {open && <tr className="bg-light"><td colSpan="5"><RequestDetails item={item} /></td></tr>}
  </>;
}

export default function RsoRequestHistory({token,active}) {
  const [page,setPage] = useState({items:[],nextCursor:null,status:"idle",key:""});
  const [request,setRequest] = useState({cursor:null,attempt:0,key:""});
  const [filters,setFilters] = useState(emptyFilters);
  const currentKey = useRef("");
  const key = active && token ? token : "";
  currentKey.current = key;
  const cursor = request.key === key ? request.cursor : null;
  useEffect(() => setFilters(emptyFilters),[key]);
  useEffect(() => {
    if (!key) {setPage({items:[],nextCursor:null,status:"idle",key:""});setRequest({cursor:null,attempt:0,key:""});return undefined;}
    const controller=new AbortController();let current=true;
    setPage((old) => ({...(cursor && old.key === key ? old : {items:[],nextCursor:null}),status:"loading",key}));
    axios.get(`${API_URL}/Dashboard/api/RsoRequestHistory`,{headers:{Authorization:`Bearer ${token}`},
      params:cursor ? {cursor} : {},signal:controller.signal,timeout:30000}).then(({data}) => {
      if (!current || currentKey.current !== key) return;
      if (!Array.isArray(data?.items) || data.items.some((item) => !item?.id || !Array.isArray(item.lines)
        || !allowedRequestTypes.has(item.requestType) || !validRequestStatus(item))) {
        const error=new Error("Invalid request history");
        error.invalidResponse=true;
        throw error;
      }
      setPage((old) => {
        const prior=cursor && old.key === key ? old.items : [];
        const ids=new Set(prior.map((item) => item.id));
        return {items:[...prior,...data.items.filter((item) => !ids.has(item.id))],nextCursor:data.nextCursor === cursor ? null : data.nextCursor,status:"ready",key};
      });
    }).catch((error) => {
      if (!current || currentKey.current !== key) return;
      // An expired sign-in shows the sign-in message instead of Try again.
      const signInExpired=isSignInExpired(error);
      const clear=error?.invalidResponse || signInExpired || error?.response?.status === 403;
      setPage((old) => ({...(old.key === key && !clear ? old : {items:[],nextCursor:null}),status:"error",key,resetCursor:clear,signInExpired}));
    });
    return () => {current=false;controller.abort();};
  },[key,token,cursor,request.attempt]);
  const visible=page.key === key ? page : {items:[],nextCursor:null,status:"loading"};
  const hasFilters=Object.values(filters).some(Boolean);
  const filteredItems=visible.items.filter((item) => matchesFilters(item,filters));
  useEffect(() => {
    if (key && hasFilters && visible.status === "ready" && visible.nextCursor) {
      setRequest((old) => ({key,cursor:visible.nextCursor,attempt:old.attempt+1}));
    }
  },[key,hasFilters,visible.status,visible.nextCursor]);
  const searchingOlder=hasFilters && !!visible.nextCursor;
  const setFilter=(field,value) => setFilters((old) => ({...old,[field]:value}));
  return <section className="rsf-request-history" aria-label="Past RSO requests">
    <h4 tabIndex="-1">Past RSO requests</h4>
    <p>RSO forecasts and material changes for your assigned properties, newest first.</p>
    <div className="d-flex flex-wrap align-items-end gap-2 mb-3">
      <Form.Group controlId="rso-history-search" className="flex-grow-1" style={{minWidth:"16rem"}}>
        <Form.Label>Search all requests</Form.Label>
        <Form.Control type="search" value={filters.search} onChange={(event) => setFilter("search",event.target.value)} placeholder="Search past requests" />
      </Form.Group>
      {hasFilters && <Button variant="outline-secondary" onClick={() => setFilters(emptyFilters)}>Clear filters</Button>}
    </div>
    <div className="rsf-history-filters" aria-label="Filter past requests">
      <Form.Group><Form.Label>Date</Form.Label><Form.Control type="date" aria-label="Filter by date" value={filters.eventDate} onChange={(event) => setFilter("eventDate",event.target.value)} /></Form.Group>
      <Form.Group><Form.Label>Property</Form.Label><Form.Control type="search" aria-label="Filter by property" value={filters.property} onChange={(event) => setFilter("property",event.target.value)} /></Form.Group>
      <Form.Group><Form.Label>Request type</Form.Label><Form.Select aria-label="Filter by request type" value={filters.requestType} onChange={(event) => setFilter("requestType",event.target.value)}>
        <option value="">All types</option><option value="forecast">Forecast</option><option value="material_change">Material change</option>
      </Form.Select></Form.Group>
      <Form.Group><Form.Label>Material</Form.Label><Form.Control type="search" aria-label="Filter by material" value={filters.material} onChange={(event) => setFilter("material",event.target.value)} /></Form.Group>
      <Form.Group><Form.Label>Forecasted Unit Turns</Form.Label><Form.Control type="search" aria-label="Filter by Forecasted Unit Turns" value={filters.turns} onChange={(event) => setFilter("turns",event.target.value)} /></Form.Group>
      <Form.Group><Form.Label>Quantity</Form.Label><Form.Control type="search" aria-label="Filter by quantity" value={filters.quantity} onChange={(event) => setFilter("quantity",event.target.value)} /></Form.Group>
    </div>
    <Table responsive className="rsf-history-table"><thead><tr>
      <th scope="col">Date</th>
      <th scope="col">Property</th>
      <th scope="col">Request type</th>
      <th scope="col">Materials</th>
      <th scope="col"><span className="visually-hidden">Details</span></th>
    </tr></thead>
      <tbody>{filteredItems.map((item) => <RequestRow key={item.id} item={item} />)}</tbody></Table>
    {visible.status === "loading" && <p role="status">{searchingOlder ? "Searching older requests…" : "Loading past requests…"}</p>}
    {visible.status === "error" && (visible.signInExpired ? <RsoSignInExpired />
      : <Alert variant="danger">Could not load past requests. <Button variant="link" onClick={() => setRequest({key,cursor:visible.resetCursor ? null : cursor,attempt:request.attempt+1})}>Try again</Button></Alert>)}
    {visible.status === "ready" && !visible.items.length && !visible.nextCursor && <p>No past RSO requests yet.</p>}
    {visible.status === "ready" && !!visible.items.length && <p role="status" aria-live="polite">{hasFilters
      ? searchingOlder ? `Showing ${filteredItems.length} match${filteredItems.length === 1 ? "" : "es"} so far. Searching older requests…` : `Showing ${filteredItems.length} of ${visible.items.length} request${visible.items.length === 1 ? "" : "s"}.`
      : `Showing ${visible.items.length} request${visible.items.length === 1 ? "" : "s"}${visible.nextCursor ? " loaded" : ""}.`}</p>}
    {visible.status === "ready" && hasFilters && !searchingOlder && !!visible.items.length && !filteredItems.length && <p>No requests match these filters.</p>}
    {visible.nextCursor && visible.status === "ready" && !hasFilters && <Button variant="outline-primary" onClick={() => setRequest({key,cursor:visible.nextCursor,attempt:request.attempt+1})}>Load more</Button>}
  </section>;
}
